import React, { useEffect, useState } from 'react'
import {
	Box,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'
import { displayDay } from '@utils'
import { dsColorNew } from '@palette'
import { heldHeightSx, useHeldHeight } from 'Pages/MyBusiness/_shared/Ui'
import { DateField } from 'Pages/MyBusiness/_shared/Filters/DateRangeFilter'

/**
 * Sprint 2 · pieza 4 — Fleet Overbooking Cascade.
 *
 * For one day (default: today), per office, show a fleet vs booked
 * matrix with the greedy upgrade cascade computed server-side. Every
 * office selected in the global filter gets its own table stacked
 * below — no cross-office aggregation.
 *
 * Reads:
 *   GET /api/my-business/fleet/cascade?day=YYYY-MM-DD&offices=A,B,C
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)

const isoToday = () => {
	const d = new Date()
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function FleetCascadeMatrix({ offices = [], defaultDay = null }) {
	const [day, setDay]         = useState(defaultDay || isoToday())
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [cardRef, heldHeight] = useHeldHeight()
	// Offices whose SIPP table is open; all start closed
	const [open, setOpen]       = useState(() => new Set())

	useEffect(() => {
		if (!day) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ day })
		if (offices.length) qs.set('offices', offices.join(','))
		fetch(`/api/my-business/fleet/cascade?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [day, offices.join(',')])

	if (error) return (
		<Paper sx={{ p: 2.5 }}>
			<AppNote severity="error">Cascade unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={6} columns={7} sx={heldHeightSx(heldHeight)}/>
	const noData = !(data.offices || []).length
	const toggle = (code) => setOpen(prev => {
		const next = new Set(prev)
		if (next.has(code)) next.delete(code)
		else                next.add(code)
		return next
	})

	return (
		<Paper ref={cardRef} sx={{ p: 2.5 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between"
			       flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
				<AppCardTitle icon="arrows-down-to-line" title="Overbooking cascade"
				              subtitle="Deficits are absorbed greedily from the closest upgradable category. Never crosses offices."/>
				<Box sx={{
					width: 190,
					'& .rs-picker-toggle': { minHeight: 40, display: 'flex', alignItems: 'center', backgroundColor: `${dsColorNew.filterInputBg} !important` },
					'& .MuiInputLabel-shrink': { px: 0.5, ml: -0.5, backgroundColor: dsColorNew.white },
					'& .MuiFormHelperText-root:empty': { display: 'none' },
				}}>
					<DateField label="Day" value={day} onChange={setDay}/>
				</Box>
			</Stack>

			{noData ? (
				<AppEmptyLine>No fleet or bookings for {displayDay(day)}. Pick another day or widen the office filter.</AppEmptyLine>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell width={36}/>
								<TableCell>Office</TableCell>
								<TableCell align="right">Fleet</TableCell>
								<TableCell align="right">Booked</TableCell>
								<TableCell align="right">Overbooked</TableCell>
								<TableCell align="right">Absorbed</TableCell>
								<TableCell align="right">Still short</TableCell>
								<TableCell/>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.offices.map(o => (
								<OfficeRows key={o.office_code} office={o}
								            isOpen={open.has(o.office_code)}
								            onToggle={() => toggle(o.office_code)}/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	)
}


const dash = <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>

// The office's state, as the chips it had: no overbook, all absorbed by upgrades, or how many are still short
function OfficeStatus({ totals }) {
	const anyOver  = totals.overbooked_units > 0
	const anyShort = totals.still_short > 0
	if (anyOver && anyShort) return <Chip size="small" color="error" label={`${fmtNum(totals.still_short)} still short`}/>
	return <Chip size="small" color="success" label={anyOver ? 'All absorbed by upgrades' : 'No overbook'}/>
}

// One office: its totals on a row that opens its SIPP table underneath
function OfficeRows({ office, isOpen, onToggle }) {
	const { totals } = office
	return (
		<>
			<TableRow hover onClick={onToggle} sx={{
				cursor: 'pointer',
				...(isOpen && { '& > .MuiTableCell-root': { backgroundColor: `${dsColorNew.pillActiveBg} !important` } }),
			}}>
				<TableCell sx={{ pr: 0 }}>
					<IconSeeker icon={isOpen ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 11, color: dsColorNew.textMuted }}/>
				</TableCell>
				<TableCell>
					<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{office.office_code}</Box>
					<Box component="span" sx={{ color: dsColorNew.textMuted, ml: 0.75 }}>{office.office_name}</Box>
				</TableCell>
				<TableCell align="right">{fmtNum(totals.fleet)}</TableCell>
				<TableCell align="right">{fmtNum(totals.booked)}</TableCell>
				<TableCell align="right">{totals.overbooked_units > 0 ? fmtNum(totals.overbooked_units) : dash}</TableCell>
				<TableCell align="right">{totals.absorbed_units > 0 ? fmtNum(totals.absorbed_units) : dash}</TableCell>
				<TableCell align="right">
					{totals.still_short > 0
						? <Box component="b" sx={{ color: dsColorNew.dangerRed }}>{fmtNum(totals.still_short)}</Box>
						: dash}
				</TableCell>
				<TableCell align="right"><OfficeStatus totals={totals}/></TableCell>
			</TableRow>
			{isOpen && (
				<TableRow>
					<TableCell/>
					<TableCell colSpan={7} sx={{ pt: 1, pb: 2, backgroundColor: `${dsColorNew.tableRowAlt} !important` }}>
						<OfficeMatrix office={office}/>
					</TableCell>
				</TableRow>
			)}
		</>
	)
}


function OfficeMatrix({ office }) {
	const { rows } = office
	return (
			<TableContainer sx={{ backgroundColor: dsColorNew.white,
			                      // The SIPP table sits inside an office row: a light header instead of the navy one
			                      '& .MuiTableHead-root .MuiTableCell-root': {
				                      backgroundColor: `${dsColorNew.pillTrackBg} !important`, color: `${dsColorNew.textMuted} !important`,
				                      fontSize: 11.5, py: 0.75,
			                      },
			                      '& .MuiTableBody-root .MuiTableCell-root': { backgroundColor: `${dsColorNew.white} !important`, py: 0.6 } }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell width={90}>SIPP</TableCell>
							<TableCell align="right" width={80}>Fleet</TableCell>
							<TableCell align="right" width={90}>Booked</TableCell>
							<TableCell align="right" width={70}>Net</TableCell>
							<TableCell align="right" width={110}>Absorbed</TableCell>
							<TableCell align="right" width={110}>Still short</TableCell>
							<TableCell>Cascade sources</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(r => {
							const over = r.net < 0
							const critical = r.still_short > 0
							const donor = r.surplus_given_to > 0
							return (
								<TableRow key={r.sipp_code}
								          sx={{ bgcolor: critical ? 'error.50'
								                        : over ? 'warning.50'
								                        : donor ? 'success.50' : undefined }}>
									<TableCell sx={{ fontWeight: 600, color: `${dsColorNew.textTitle} !important` }}>{r.sipp_code}</TableCell>
									<TableCell align="right">{fmtNum(r.fleet)}</TableCell>
									<TableCell align="right">{fmtNum(r.booked)}</TableCell>
									<TableCell align="right"
									           sx={{ fontWeight: 600,
									                 color: `${r.net < 0 ? dsColorNew.dangerRed
									                       : r.net > 0 ? dsColorNew.successText : dsColorNew.textMuted} !important` }}>
										{r.net > 0 ? `+${r.net}` : r.net}
									</TableCell>
									<TableCell align="right">
										{r.absorbed_from_upgrades > 0
											? <b>{fmtNum(r.absorbed_from_upgrades)}</b>
											: <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>}
									</TableCell>
									<TableCell align="right">
										{r.still_short > 0
											? <Box component="b" sx={{ color: dsColorNew.dangerRed }}>{fmtNum(r.still_short)}</Box>
											: <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>}
									</TableCell>
									<TableCell>
										{r.sources.length > 0 ? (
											<Box display="flex" flexWrap="wrap" gap={0.5}>
												{r.sources.map((s, i) => (
													<Tooltip key={i}
													         title={`Took ${s.units} unit(s) from ${s.from_sipp}`}>
														<Chip size="small" variant="outlined"
														      label={`+${s.units} ← ${s.from_sipp}`}
														      sx={{ fontSize: 11.5, height: 22 }}/>
													</Tooltip>
												))}
											</Box>
										) : donor ? (
											<Typography sx={{ fontSize: 12, color: dsColorNew.successText }}>
												Donated {r.surplus_given_to} unit(s) upward
											</Typography>
										) : (
											<Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
										)}
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
	)
}
